import Polyglot from 'node-polyglot';
import { getUserPreferredLanguage } from "./helpers";

/**
 * A factory function that builds and returns our translation tool.
 * @param {string} appRootUrl   - The URL of the application root.
 * @param {string} appSlug      - The app slug.
  * @param {string} localePath  - The directory path off root that the locale files are stored in.
 * @returns {Translator}        - Our translation tool.
 */
const buildTranslator = async (appConfig, localePath, localesFromDatabase) => {
    const userPreferredLanguage = getUserPreferredLanguage(appConfig, localesFromDatabase);
    const translator = new Translator();
    await translator.setLanguage(userPreferredLanguage, localePath);

    return translator;
}

/**
 * @class Our translation tool.
 */
class Translator {
    #polyglot;
    constructor() {
    }

    /**
     * Sets the language our translation tool will use when calling {@link translate}
     * @param {string} language     - A string language tag and region subtag ({@link https://www.w3.org/International/articles/language-tags/ see the w3 docs}).
     * @param {string} localePath   - The directory path off root that the locale files are stored in.
     */
    async setLanguage(language, localePath) {
        const englishPhrases = await System.import(`${__dirname}${localePath}/en-US.json`);
        const fallbackPolyglot = new Polyglot({phrases: englishPhrases.default});

        const selectedLanguagePhrases = await System.import(`${__dirname}${localePath}/${language}.json`);
        this.#polyglot = new Polyglot({
            phrases: selectedLanguagePhrases.default,
            allowMissing: true,
            onMissingKey: (key) => fallbackPolyglot.t(key)
        });
    }

    /**
     * Translates the given key using the language set via {@link setLanguage}
     * @param {string} key  - The key to find the translation for.
     * @returns {string}    - The translation value.
     */
    translate(key) {
        return this.#polyglot.t(key);
    }
}

export default buildTranslator;
